import { Disclosure, DisclosureContent, DisclosureProvider, useFormContext } from '@ariakit/react'
import { useContext } from 'react'
import styled from 'styled-components'

import { LoadingSpinner } from '@syconium/little-miss-figgy'

import { VariantSelectionColorRadioGroup } from '../../../../app/_components/forms/VariantSelectionColorField'
import {
  VariantSelectionFormContext,
  VariantSelectionFormState,
} from '../../../../app/_components/forms/VariantSelectionForm'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { useEmbroidery } from '../../../../containers/EmbroideryContainer/EmbroideryProvider'
import {
  AccordionCarat,
  AccordionDetails,
  AccordionHeader,
  AccordionHeading,
  AccordionSubHeading,
  EmbroideryAccordionOption,
} from '../styles'

type EmbroideryColorSelectorHeaderProps = {
  displayValue?: string
  isLoading?: boolean
}

const EmbroideryColorSelectorHeader = ({ displayValue }: EmbroideryColorSelectorHeaderProps) => {
  const { status: embroideryDataStatus } = useEmbroidery()
  const translations = useTranslation()
  const colorTitle = translations.embroidery.modal.colorOptions.title
  const loadingLabel = translations.magnolia.general.loading

  return (
    <AccordionHeader>
      <HeaderText>
        <HeaderRow>
          <AccordionHeading>{colorTitle}</AccordionHeading>
          {embroideryDataStatus === 'pending' ? (
            <InlineSpinner label={loadingLabel} size='14px' />
          ) : null}
        </HeaderRow>
        <AccordionSubHeading>{displayValue}</AccordionSubHeading>
      </HeaderText>
      <AccordionCarat />
    </AccordionHeader>
  )
}

export const EmbroideryColorSelector = () => {
  const { product } = useContext(VariantSelectionFormContext)!
  const formContext = useFormContext()
  const value = formContext!.useValue<VariantSelectionFormState['color']>('color')
  const option = product.options.colors?.find(colorOption => colorOption.color.id === value)

  return (
    <li>
      <EmbroideryColorSelectorOption>
        <DisclosureProvider>
          <Disclosure render={htmlProps => <AccordionHeader {...htmlProps} />}>
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
            <EmbroideryColorSelectorHeader displayValue={option?.color?.displayValue} />
          </Disclosure>
          <DisclosureContent>
            <AccordionDetails>
              <VariantSelectionColorRadioGroup priceBucketing='compact' />
            </AccordionDetails>
          </DisclosureContent>
        </DisclosureProvider>
      </EmbroideryColorSelectorOption>
    </li>
  )
}

const EmbroideryColorSelectorOption = styled(EmbroideryAccordionOption)`
  border-top: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.subtle};
`

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(1)};
`

const InlineSpinner = styled(LoadingSpinner)`
  flex-shrink: 0;
`
